import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { CanvasEdge, CanvasNode } from '../../../../hooks/workflows/useWorkflowOperations';

interface EdgeSectionProps {
  nodeId: string;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  onEdgeAdd: (targetNodeId: string, condition: any) => void;
  onEdgeDelete: (edgeId: string) => void;
  onEdgeUpdate: (edges: CanvasEdge[]) => void;
  onCreateNewNode?: () => void;
}

const EDGE_CONDITIONS = {
  'always': 'Always',
  'success': 'Success',
  'failure': 'Failure',
  'email_reply_received': 'Email Reply Received',
  'email_reply_timeout': 'Email Reply Timeout',
  'data_match': 'Data Match',
  'classification_match': 'Classification Match',
  'confidence_threshold': 'Confidence Threshold'
};

export const EdgeSection: React.FC<EdgeSectionProps> = ({
  nodeId,
  nodes,
  edges,
  onEdgeAdd,
  onEdgeDelete,
  onEdgeUpdate,
  onCreateNewNode
}) => {
  // Get edges from this node
  const outgoingEdges = edges.filter(edge => edge.source === nodeId);
  
  // Get available target nodes (exclude self, start node, and already connected nodes)
  const connectedTargets = new Set(outgoingEdges.map(edge => edge.target));
  const availableTargets = nodes.filter(node => 
    node.id !== nodeId && 
    node.id !== '-1' && // Exclude start node
    !connectedTargets.has(node.id)
  );

  const handleAddEdge = () => {
    if (availableTargets.length > 0) {
      // Add edge to first available target with default condition
      onEdgeAdd(availableTargets[0].id, { type: 'always' });
    }
  };

  const updateEdgeCondition = (edgeId: string, conditionUpdate: any) => {
    const updatedEdges = edges.map(edge => 
      edge.id === edgeId ? { ...edge, condition: { ...edge.condition, ...conditionUpdate } } : edge
    );
    onEdgeUpdate(updatedEdges);
  };

  const handleConditionChange = (edgeId: string, conditionType: string) => {
    let condition: Record<string, any> = { type: conditionType };
    
    // Add default parameters for conditions that require them
    if (conditionType === 'data_match') {
      condition = { type: conditionType, path: 'result.field', operator: '==', value: 'expected' };
    } else if (conditionType === 'classification_match') {
      condition = { type: conditionType, classification: 'positive' };
    } else if (conditionType === 'confidence_threshold') {
      condition = { type: conditionType, threshold: 0.8 };
    }
    
    const updatedEdges = edges.map(edge => {
      if (edge.id === edgeId) {
        const newId = `${condition.type}-${edge.source}-${edge.target}`;
        return { ...edge, id: newId, condition };
      }
      return edge;
    });
    onEdgeUpdate(updatedEdges);
  };

  const handleTargetChange = (edgeId: string, newTargetId: string) => {
    // Update the edge target atomically to prevent duplicates
    const updatedEdges = edges.map(edge => {
      if (edge.id === edgeId) {
        // Generate new ID with condition type, source, and new target
        const newId = `${edge.condition?.type || 'always'}-${edge.source}-${newTargetId}`;
        return { ...edge, id: newId, target: newTargetId };
      }
      return edge;
    });
    onEdgeUpdate(updatedEdges);
  };

  const getTargetNodeName = (targetId: string) => {
    const targetNode = nodes.find(n => n.id === targetId);
    return targetNode?.data.name || targetNode?.type || 'Unknown';
  };

  if (nodeId === '-1') {
    return null; // Don't show edge management for start node
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 flex-1">
          Outgoing Edges
        </h4>
        {availableTargets.length > 0 && (
          <Button
            onClick={handleAddEdge}
            size="sm"
            variant="ghost"
            className="ml-2"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Edge
          </Button>
        )}
      </div>

      {outgoingEdges.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm">
          No outgoing edges. Add an edge to connect this node to another.
        </div>
      ) : (
        <div className="space-y-3">
          {outgoingEdges.map((edge) => (
            <div key={edge.id} className="border border-gray-200 rounded-lg p-3 space-y-3 relative">
              {/* Delete button - positioned at top right */}
              <button
                onClick={() => onEdgeDelete(edge.id)}
                className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded transition-colors z-10"
                title="Delete Edge"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Edge target selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Target Node
                </label>
                <div className="relative">
                  <select
                    value={edge.target}
                    onChange={(e) => {
                      if (e.target.value === '__create_new__') {
                        onCreateNewNode?.();
                      } else {
                        handleTargetChange(edge.id, e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    style={{ maxWidth: '100%' }}
                  >
                    <option value={edge.target}>{getTargetNodeName(edge.target)}</option>
                    {availableTargets.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.data?.name || node.type}
                      </option>
                    ))}
                    {onCreateNewNode && (
                      <option value="__create_new__" className="font-medium text-blue-600">
                        + Create New Node
                      </option>
                    )}
                  </select>
                </div>
              </div>

              {/* Condition type selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Condition Type
                </label>
                <div className="relative">
                  <select
                    value={edge.condition?.type || 'always'}
                    onChange={(e) => handleConditionChange(edge.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    style={{ maxWidth: '100%' }}
                  >
                    {Object.entries(EDGE_CONDITIONS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Additional parameters for specific condition types */}
              {edge.condition?.type === 'data_match' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Path</label>
                      <input
                        type="text"
                        value={edge.condition.path || ''}
                        onChange={(e) => updateEdgeCondition(edge.id, { path: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                        placeholder="result.field"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Operator</label>
                      <select
                        value={edge.condition.operator || '=='}
                        onChange={(e) => updateEdgeCondition(edge.id, { operator: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                      >
                        <option value="==">Equals</option>
                        <option value="!=">Not Equals</option>
                        <option value=">">Greater Than</option>
                        <option value=">=">Greater Than or Equal</option>
                        <option value="<">Less Than</option>
                        <option value="<=">Less Than or Equal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
                      <input
                        type="text"
                        value={edge.condition.value || ''}
                        onChange={(e) => updateEdgeCondition(edge.id, { value: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                        placeholder="expected"
                      />
                    </div>
                  </div>
                </div>
              )}

              {edge.condition?.type === 'classification_match' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Classification</label>
                  <div className="relative">
                    <select
                      value={edge.condition.classification || 'positive'}
                      onChange={(e) => updateEdgeCondition(edge.id, { classification: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                    >
                      <option value="positive">Positive</option>
                      <option value="negative">Negative</option>
                      <option value="requires_followup">Requires Followup</option>
                    </select>
                  </div>
                </div>
              )}

              {edge.condition?.type === 'confidence_threshold' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Threshold (0-1)</label>
                  <input
                    type="number"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={edge.condition.threshold || 0.8}
                    onChange={(e) => updateEdgeCondition(edge.id, { threshold: Number(e.target.value) || edge.condition.threshold || null })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};